import { IPaginationReturn } from '../../interfaces/IPaginationReturn';
import { useRequest } from '../providers/useRequest';
import AppError from '../../errors/AppError';
import ISubsidiary from '../../interfaces/ISubsidiary';

export interface IFilterSubsidiary {
  id?: number | null;
}

const useSubsidiaryService = () => {
  const Subsidiary = useRequest<ISubsidiary>();
  const path = 'employee/subsidiary';

  const list = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    direction: 'asc' | 'desc',
    filter?: IFilterSubsidiary,
  ): Promise<IPaginationReturn<ISubsidiary[]>> => {
    return await Subsidiary.postAndGetManyPaginated({
      path: `${path}/list?perPage=${perPage}&currentPage=${currentPage}&orderBy=${orderField}&orderDirection=${direction}`,
      sendAuthorization: true,
      body: { ...filter },
    })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const findById = async (id: number): Promise<ISubsidiary> => {
    return await Subsidiary.getOne({
      path: `${path}/${id}`,
      sendAuthorization: true,
    })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listSubsidiaryByName = async (name: string): Promise<ISubsidiary[]> => {
    return await Subsidiary.getMany({
      path: `${path}/findByName/${name}`,
      sendAuthorization: true,
    })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listSubsidiaryByCompanyId = async (id: number): Promise<ISubsidiary[]> => {
    return await Subsidiary.getMany({
      path: `${path}/listByCompanyId/${id}`,
      sendAuthorization: true,
    })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listSubsidiarySearch = async (filter: string): Promise<ISubsidiary[]> => {
    if (filter === '') {
      filter = ' ';
    }

    /** 
if (companyId === 0) {
      companyId = 0;
    }
*/

    const url = filter
      ? `${path}/search?perPage=300&currentPage=1&orderBy=code&orderDirection=asc&filter=${filter}`
      : `${path}/search?perPage=300&currentPage=1&orderBy=code&orderDirection=asc&filter= `;

    return await Subsidiary.getManyPaginated({
      path: url,
      sendAuthorization: true,
    })
      .then(result => {
        return result?.data;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const create = async ({ code, name }: ISubsidiary): Promise<ISubsidiary> => {
    return await Subsidiary.post({
      path: `${path}`,
      sendAuthorization: true,
      body: { code, name },
    })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const update = async (id: number, { code, name }: ISubsidiary): Promise<ISubsidiary> => {
    return await Subsidiary.put({
      path: `${path}/${id}`,
      sendAuthorization: true,
      body: { code, name },
    })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const remove = async (id: number): Promise<void> => {
    return await Subsidiary.remove({
      path: `${path}/${id}`,
      sendAuthorization: true,
    })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  return {
    list,
    listSubsidiaryByName,
    findById,
    listSubsidiaryByCompanyId,
    listSubsidiarySearch,
    create,
    update,
    remove,
  };
};

export { useSubsidiaryService };
